/**
 * Funnel locale registry — DERIVED from the marketing locale registry.
 *
 * DIT-399: the funnel used to hand-maintain its own 16-code list, which had
 * drifted from the marketing site's live locales. That mattered because the
 * CloudFront locale function only knows the marketing registry: it 302s any path
 * whose first segment is not a live marketing locale to `/<default>/<path>`,
 * which then falls through the marketing `:lang/*` splat to the 404 screen. Every
 * code the two lists disagreed on was therefore a dead signup URL —
 * `/nl-BE/plan`, `/nl-NL/plan`, `/fr/plan`, `/en-NZ/plan` died at the edge, and
 * `/fr-be/plan`, `/en-be/plan` died in `FunnelGate`. Belgium is the primary
 * market and is served by nl / fr-be / en-be, so only `nl` reached the funnel.
 *
 * `LIVE_LOCALES` is now the single source of truth for what is ROUTABLE. The
 * funnel only decides which translation bundle backs each live locale — see
 * `src/shared/i18n/locales/index.ts`.
 *
 * The bundles behind the retired prefixes stay on disk (16 directories, per the
 * project i18n convention); they are simply no longer routable on their own.
 */
import { LIVE_LOCALES, SOURCE_LOCALE, isLiveLocale } from '@/marketing/i18n/locales';
import legacyLangAliases from './legacy-lang-aliases.json';

/** Routable funnel locales === the marketing live locales. Never hand-maintained. */
export const SUPPORTED_LANGS: readonly string[] = LIVE_LOCALES.map((l) => l.code);

export type Lang = string;

/**
 * Fallback language for lang-less entry points — notably Stripe's return URL,
 * which comes back as `/done?…` with no prefix (see UC-AUTH-DO-006).
 *
 * This is `SOURCE_LOCALE` ("en"), NOT the marketing `DEFAULT_LOCALE` ("nl"): the
 * marketing site defaults to Belgium for a cold visitor, but a lang-less funnel
 * URL means we lost the context of a session that had already chosen a language,
 * and English is the safer neutral landing than silently switching a returning
 * customer to Dutch.
 */
export const DEFAULT_LANG: Lang = SOURCE_LOCALE;

/** Is `code` a routable funnel locale? */
export const isSupportedLang = (code?: string | null): boolean => isLiveLocale(code);

/**
 * Retired funnel-only prefixes → the live locale that now serves them.
 *
 * `nl-BE`/`nl-NL` both collapse onto marketing `nl`, which IS the Belgian
 * (Flemish) locale — `market: "belgium"` in the registry — and is backed by the
 * Flemish funnel bundle. Bare `fr` maps to `fr-be` because Belgian French is the
 * only French market we serve today.
 *
 * The data lives in `legacy-lang-aliases.json` because the CloudFront geo
 * function needs the SAME map: in-app aliasing only fires once the edge lets the
 * request through, and the edge otherwise 302s these prefixes to
 * `/en/<code>/…` — a 404 (DIT-399). `scripts/sync-geo-kvs.py` reads that JSON at
 * deploy and writes it into the edge KeyValueStore, so the two cannot drift.
 */
export const LEGACY_LANG_ALIASES: Readonly<Record<string, string>> = legacyLangAliases;

/**
 * Canonical live locale for a URL segment: the segment itself when it is already
 * live, the alias target for a retired prefix, otherwise `null` (the segment is
 * not a locale at all — e.g. a lang-less `/done`).
 */
/** The alias map, keyed lowercase as well: the JSON keys carry the historical
 *  casing (`nl-BE`), while several consumers — the launcher's language
 *  resolution among them — fold the segment before asking. Without this the
 *  two resolvers split on exactly the retired prefixes the map exists for. */
const LEGACY_LANG_ALIASES_FOLDED: Readonly<Record<string, string>> = Object.fromEntries(
  Object.entries(legacyLangAliases).map(([code, target]) => [code.toLowerCase(), target]),
);

export function canonicalLang(code?: string | null): Lang | null {
  if (!code) return null;
  if (isLiveLocale(code)) return code;
  const alias = LEGACY_LANG_ALIASES[code] ?? LEGACY_LANG_ALIASES_FOLDED[code.toLowerCase()];
  return alias && isLiveLocale(alias) ? alias : null;
}

/**
 * Languages the API accepts for `Company.preferred_language` — the `Company.Language`
 * choices in `done-it-api/apps/cauth/models.py`. This is a PRODUCT vocabulary and it is
 * NOT the same list as the marketing locales above: it predates them, it is what every
 * Django `.po` directory and every dashboard/mobile language picker is keyed on, and it
 * is changed by a migration rather than by adding a URL prefix.
 */
const API_LANGUAGES: readonly string[] = [
  'nl-nl', 'nl-be', 'en', 'en-nz', 'fr', 'es', 'pt',
  'tr', 'de', 'no', 'lt', 'pl', 'ro', 'bg', 'it',
];

/**
 * Live locales whose code is not the product language they mean. All three are the
 * Belgian ones, which is exactly why this matters: `nl` is the marketing code for
 * Flemish (the registry's `market: "belgium"`, backed by the `nl-BE` bundle), and the
 * region-suffixed `fr-be` / `en-be` exist to name a market, not a translation.
 */
const PREFERRED_LANGUAGE_OVERRIDES: Readonly<Record<string, string>> = {
  nl: 'nl-be',
  'fr-be': 'fr',
  'en-be': 'en',
};

/**
 * The URL locale, translated into a language the API will accept.
 *
 * The funnel used to post `i18n.resolvedLanguage` verbatim, so `/nl`, `/fr-be` and
 * `/en-be` — every Belgian visitor — were rejected by the registration serializer's
 * `ChoiceField` with a 400 the form had nowhere to show (DIT-478). The funnel is
 * routable wherever the marketing site publishes a locale, so the two vocabularies will
 * drift again; resolving here means the next drift costs a wrong language on a company
 * record, never a lost sign-up.
 *
 * Resolution order: explicit override, exact match, the base language (or the first
 * regional variant of it), then `en`. Total, and never throws.
 */
export function toPreferredLanguage(code?: string | null): string {
  const lower = (code ?? '').trim().toLowerCase();
  if (!lower) return DEFAULT_LANG;

  const override = PREFERRED_LANGUAGE_OVERRIDES[lower];
  if (override) return override;
  if (API_LANGUAGES.includes(lower)) return lower;

  const base = lower.split('-')[0];
  const nearest = API_LANGUAGES.find(
    (lang) => lang === base || lang.startsWith(`${base}-`),
  );
  return nearest ?? DEFAULT_LANG;
}

// `review` step was folded into payment (spec §3.4); the namespace
// is gone too. Keep the list in lockstep with the JSON files on
// disk and with each <lang>/index.ts barrel.
export const NAMESPACES = [
  'common',
  'plan',
  'confirm',
  'account',
  'payment',
  'done',
] as const;
